import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchCustomerDetail } from '@/hooks/useCustomers';
import type { CustomerDetail } from '@/hooks/useCustomers';
import { StatusBadge } from '@/components/base/StatusBadge';
import { theme } from '@/lib/theme';

interface Props {
  customerId: string | null;
  onClose: () => void;
}

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount);
}

export default function CustomerDetailModal({ customerId, onClose }: Props) {
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) { setDetail(null); return; }
    setLoading(true);
    fetchCustomerDetail(customerId)
      .then(setDetail)
      .finally(() => setLoading(false));
  }, [customerId]);

  if (!customerId) return null;

  return (
    <AnimatePresence>
      {customerId && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border pointer-events-auto overflow-hidden"
              style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-6 py-5 border-b flex-shrink-0"
                style={{ borderColor: theme.colors.border }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: theme.colors.primaryMuted }}
                  >
                    <i className="ri-user-line text-base" style={{ color: theme.colors.primary }} />
                  </div>
                  <div>
                    <h2 className="text-white text-base font-semibold">
                      {loading ? 'Cargando...' : (detail?.name ?? 'Cliente')}
                    </h2>
                    <p className="text-white/40 text-xs mt-0.5">Historial completo</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/40 hover:text-white transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-base" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {loading && (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-loader-4-line text-white/40 text-xl animate-spin" />
                    </div>
                  </div>
                )}

                {!loading && detail && (
                  <>
                    {/* Contact info */}
                    <div
                      className="rounded-xl p-4 border"
                      style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                    >
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-3">Información de contacto</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <p className="text-white/40 text-xs mb-1">Email</p>
                          <p className="text-white text-sm break-all">{detail.email}</p>
                        </div>
                        <div>
                          <p className="text-white/40 text-xs mb-1">Teléfono</p>
                          <p className="text-white text-sm">{detail.phone ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-white/40 text-xs mb-1">Cliente desde</p>
                          <p className="text-white text-sm">{formatDate(detail.created_at.slice(0, 10))}</p>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div
                        className="rounded-xl p-4 border text-center"
                        style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                      >
                        <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Citas</p>
                        <p className="text-white text-2xl font-bold">{detail.total_appointments}</p>
                      </div>
                      <div
                        className="rounded-xl p-4 border text-center"
                        style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                      >
                        <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Total gastado</p>
                        <p className="text-white text-lg font-bold">{formatCurrency(detail.total_spent)}</p>
                      </div>
                      <div
                        className="rounded-xl p-4 border text-center"
                        style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                      >
                        <p className="text-white/35 text-xs uppercase tracking-wider mb-2">Última visita</p>
                        <p className="text-white text-sm font-medium">
                          {detail.last_visit ? formatDate(detail.last_visit) : '—'}
                        </p>
                      </div>
                    </div>

                    {/* Appointment history */}
                    <div>
                      <p className="text-white/35 text-xs uppercase tracking-wider mb-3">Historial de citas</p>
                      {detail.appointments.length === 0 ? (
                        <div
                          className="rounded-xl p-8 border text-center"
                          style={{ backgroundColor: theme.colors.bgBase, borderColor: theme.colors.border }}
                        >
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-3">
                            <i className="ri-calendar-line text-white/25 text-lg" />
                          </div>
                          <p className="text-white/30 text-sm">Sin citas registradas</p>
                        </div>
                      ) : (
                        <div
                          className="rounded-xl border overflow-hidden"
                          style={{ borderColor: theme.colors.border }}
                        >
                          <table className="w-full">
                            <thead>
                              <tr
                                className="border-b"
                                style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.bgBase }}
                              >
                                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-4 py-3">Fecha</th>
                                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Hora</th>
                                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-4 py-3">Servicio</th>
                                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-4 py-3">Estado</th>
                                <th className="text-right text-white/35 text-xs font-medium uppercase tracking-wider px-4 py-3">Pagado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.appointments.map((appt, idx) => (
                                <tr
                                  key={appt.id}
                                  className={`border-b hover:bg-white/2 transition-colors ${idx === detail.appointments.length - 1 ? 'border-b-0' : ''}`}
                                  style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.bgSurface }}
                                >
                                  <td className="px-4 py-3">
                                    <span className="text-white/80 text-sm font-mono">{formatDate(appt.appointment_date)}</span>
                                  </td>
                                  <td className="px-4 py-3 hidden sm:table-cell">
                                    <span className="text-white/50 text-sm font-mono">{appt.start_time?.slice(0, 5) ?? '—'}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-white/70 text-sm">{appt.service_name ?? '—'}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <StatusBadge status={appt.status} paymentStatus={appt.payment_status} />
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <span className="text-white/70 text-sm font-mono">
                                      {appt.amount_paid > 0 ? formatCurrency(appt.amount_paid) : '—'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!loading && !detail && (
                  <div className="flex items-center justify-center py-16">
                    <p className="text-white/30 text-sm">No se pudo cargar la información del cliente</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                className="flex justify-end px-6 py-4 border-t flex-shrink-0"
                style={{ borderColor: theme.colors.border }}
              >
                <button
                  onClick={onClose}
                  className="px-5 py-2 rounded-lg text-sm text-white/50 hover:text-white hover:bg-white/8 transition-all cursor-pointer whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
